import type { AxiosInstance, AxiosResponse } from 'axios';
import type { PaginationParams, QueryParams, RequestOptions } from '@/libs/api/types';

function cleanParams(params?: QueryParams): Record<string, string | number | boolean> | undefined {
  if (!params) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(params).filter(
      (entry): entry is [string, string | number | boolean] =>
        entry[1] !== undefined && entry[1] !== null,
    ),
  );
}

export function createApiFactory(client: AxiosInstance) {
  return {
    async get<T>(url: string, params?: QueryParams, options?: RequestOptions): Promise<T> {
      const response = await client.get<T>(url, {
        params: cleanParams(params),
        ...options,
      });
      return response.data;
    },

    async getWithResponse<T>(
      url: string,
      params?: QueryParams,
      options?: RequestOptions,
    ): Promise<AxiosResponse<T>> {
      return client.get<T>(url, {
        params: cleanParams(params),
        ...options,
      });
    },

    async getList<T>(
      url: string,
      params?: QueryParams & PaginationParams,
      options?: RequestOptions,
    ): Promise<T> {
      const response = await client.get<T>(url, {
        params: cleanParams(params),
        ...options,
      });
      return response.data;
    },

    async post<T, D = unknown>(url: string, data?: D, options?: RequestOptions): Promise<T> {
      const response = await client.post<T>(url, data, options);
      return response.data;
    },

    async put<T, D = unknown>(url: string, data?: D, options?: RequestOptions): Promise<T> {
      const response = await client.put<T>(url, data, options);
      return response.data;
    },

    async patch<T, D = unknown>(url: string, data?: D, options?: RequestOptions): Promise<T> {
      const response = await client.patch<T>(url, data, options);
      return response.data;
    },

    async delete<T>(url: string, options?: RequestOptions): Promise<T> {
      const response = await client.delete<T>(url, options);
      return response.data;
    },

    async upload<T>(url: string, formData: FormData, options?: RequestOptions): Promise<T> {
      const response = await client.post<T>(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        ...options,
      });
      return response.data;
    },
  };
}

export type ApiFactoryInstance = ReturnType<typeof createApiFactory>;
